/**
 * Editing one Item.
 *
 * The form is built from two things: the collection's schema, which the panel
 * reads out of `content/` rather than keeping a copy of, and `shared/forms.js`,
 * which says what to call each field and what order to ask for things in. A
 * field added to the schema and forgotten here still appears, in a section
 * called "Other" — the panel should never be the reason the clinic cannot edit
 * something.
 *
 * ## The three buttons, and the rule behind them
 *
 * **Nothing changes a live page except Publish.** Everything below follows from
 * that, and it is worth stating because the obvious implementation gets it
 * wrong: a "Save draft" that writes to `main` does not change a *draft*, but on
 * a page that is already published it changes the website — while saying it has
 * not.
 *
 * **Save** on a draft writes to `main` with `isDraft: true`; the build leaves
 * drafts out, so no page changes and a colleague can pick the work up. **Save**
 * on a published page writes to that page's own branch instead, and says so —
 * the live page keeps showing the old version until somebody publishes.
 *
 * **Preview** builds that branch at a URL of its own, through the existing
 * deploy workflow. That is the real preview — the same Eleventy, the same
 * guards, the same CSS — rather than an approximation rendered here.
 *
 * **Publish** takes what the branch holds, applies anything typed since, and
 * writes it to `main`. Editors do not have this button; they have "Ask a
 * Publisher", which puts the item on the dashboard of everyone who does.
 *
 * ## Unchanged fields are not rewritten
 *
 * Only fields the person actually touched are sent. A body loaded into the
 * editor and left alone is never re-serialised, so fixing a Provider's job
 * title cannot quietly re-mark-up their biography.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../api.js";
import { BuildStatus, FieldMessages, ImageField, Message, Meter, SearchPreview } from "../components/bits.jsx";
import RichText from "../editor/rich-text.jsx";
import { go } from "../app.jsx";
import { collectionBySlug, itemUrl } from "../../shared/collections.js";
import { SCHEDULED_NOTICE, formatDateTime, isFuture, parseDayInput, parseLocalInput, toDayInput, toLocalInput } from "../../shared/dates.js";

export default function ItemForm({ collection, id, session, may }) {
  const entry = collectionBySlug(collection);
  const [loaded, setLoaded] = useState(null);
  const [edits, setEdits] = useState({});
  const [findings, setFindings] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [watching, setWatching] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(async () => {
    setLoaded(null);
    setEdits({});
    try {
      const data = await api.get(`collections/${collection}/${id}`);
      setLoaded(data);
      setFindings(data.findings);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not open it.");
    }
  }, [collection, id]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useCallback(
    (slug) => (slug in edits ? edits[slug] : loaded?.item?.fieldData?.[slug] ?? null),
    [edits, loaded]
  );
  const set = (slug, next) => setEdits((current) => ({ ...current, [slug]: next }));

  const dirty = Object.keys(edits).length > 0;

  // Warn before the tab closes on unsaved work. The panel is a thing people
  // leave open in a tab for an afternoon.
  useEffect(() => {
    if (!dirty) return undefined;
    const onLeave = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  async function act(action, extra = {}) {
    setBusy(action);
    setError(null);
    setNotice(null);
    setWatching(null);
    try {
      const result = await api.put(`collections/${collection}/${id}`, {
        action,
        fields: edits,
        fileSha: loaded.fileSha,
        original: loaded.item,
        ...extra,
      });
      setFindings(result.findings || []);
      setEdits({});
      setLoaded((current) => ({ ...current, item: result.item }));
      setWatching({ branch: result.branch, preview: result.preview, previewPath: result.previewPath, live: result.live });
      setNotice({
        level: "ok",
        text:
          action === "publish"
            ? "Published. The live site updates when the build finishes."
            : action === "unpublish"
            ? entry.urlPrefix
              ? "Unpublished. The old address now redirects, so nobody's link breaks."
              : "Unpublished. It is off the site once the build finishes."
            : result.where === "branch"
            ? "Saved to this page's own preview. The live page has not changed — publish when you are ready."
            : "Saved as a draft. It is not on the live site.",
      });
      // The file has moved on; reload so the next save compares against it.
      const fresh = await api.get(`collections/${collection}/${id}`);
      setLoaded((current) => ({ ...fresh, item: current.item }));
      // A draft save judges no rules — they are for publishing — but the
      // warnings the rules would give are worth seeing now, next to the
      // fields, rather than on the day somebody presses Publish. The reload
      // judged the Item as a publish would; its warnings join the save's.
      setFindings((mine) => {
        const seen = new Set(mine.map((f) => `${f.field}|${f.message}`));
        return [...mine, ...(fresh.findings || []).filter((f) => f.level === "warning" && !seen.has(`${f.field}|${f.message}`))];
      });
    } catch (problem) {
      if (problem instanceof ApiError && problem.status === 422) {
        setFindings(problem.findings);
        // A refusal about the whole item — it is pinned on the home page, say
        // — has no field to sit under, so it is said here.
        const whole = (problem.findings || []).find((f) => f.field === null || f.field === undefined);
        setError(whole ? whole.message : "Some fields need attention. They are marked below.");
      } else {
        setError(problem instanceof ApiError ? problem.message : "Could not save.");
      }
    } finally {
      setBusy(null);
    }
  }

  async function askPublisher() {
    const note = window.prompt("Anything the Publisher should know? (optional)") ?? "";
    await api.post("pending", { collection, id, note });
    setNotice({ level: "ok", text: "Asked. It is on the Publishers' dashboard now." });
  }

  async function unpublish() {
    // An Item with no page of its own has no address to redirect.
    const address = itemUrl(collection, loaded.item.fieldData.slug);
    const where = address
      ? window.prompt(
          `Where should ${address} send people now?\n\n` +
            `The old address stays working as a permanent redirect — it is in search results and other people's links.`,
          entry.defaultDestination || "/"
        )
      : "";
    if (where === null) return;
    // The reasons the registry suggests, numbered, or the person's own words.
    // A number picks a preset; anything else is kept as typed.
    const presets = entry.unpublishReasons || [];
    const menu = presets.map((text, n) => `  ${n + 1}. ${text}`).join("\n");
    const typed = window.prompt(
      `Why, for whoever reads this in a year?${presets.length ? `\n\nType a number, or your own words:\n${menu}` : ""}`
    );
    if (typed === null) return;
    const picked = presets[Number(typed.trim()) - 1];
    const reason = picked || typed.trim();
    await act("unpublish", { redirectTo: where, reason });
  }

  const showHistory = async () => {
    setHistory("loading");
    try {
      const { commits } = await api.get(`history/${collection}?item=${encodeURIComponent(id)}`);
      setHistory(commits);
    } catch {
      setHistory([]);
    }
  };

  if (error && !loaded) return <Message level="error">{error}</Message>;
  if (!loaded) return <div className="empty">Loading…</div>;

  const { item, schema, form, references, unpublishedEdit, previewBranch, whereShown, placements } = loaded;
  const published = !item.isDraft && !item.isArchived;
  const everPublished = Boolean(item.lastPublished) || published;
  const address = itemUrl(collection, item.fieldData.slug);
  const url = address ? `${address}/` : null;

  // The fields as they stand now — what is loaded, with this sitting's edits
  // over it — for the rules that derive one thing from another.
  const current = { ...(item.fieldData || {}), ...edits };
  const titleValue = value("seo-title") || entry.seoTitle(current);
  const descriptionValue = value(entry.descriptionField);
  // Where a pageless Item appears, named: the site's own list of host pages
  // when the API worked it out, else the one path it could name.
  const appearsOn = Array.isArray(placements) && placements.length
    ? placements.map((p) => (p.url === "/questions/" ? "/questions" : p.title)).join(", ")
    : whereShown;

  return (
    <>
      <div className="head">
        <div>
          <h1>{entry.title(current) || "Untitled"}</h1>
          <p className="sub">
            <span className={`pill ${published ? "published" : item.isArchived ? "archived" : "draft"}`}>
              {published ? "published" : item.isArchived ? "archived" : "draft"}
            </span>{" "}
            {url ? (
              <>
                <span className="mono">{url}</span>{" "}
                {everPublished && <span className="hint">· the address is frozen (ADR-0003)</span>}
              </>
            ) : (
              <span className="hint">
                No page of its own{appearsOn ? <> · Appears on: <span className="mono">{appearsOn}</span></> : null}
              </span>
            )}
          </p>
        </div>
        <div className="row">
          <a className="btn" href={`#/${collection}`}>
            ← All {entry.many.toLowerCase()}
          </a>
        </div>
      </div>

      <Message level="error">{error}</Message>
      {notice && <Message level={notice.level}>{notice.text}</Message>}
      {watching && (
        <BuildStatus branch={watching.branch} preview={watching.preview} previewPath={watching.previewPath} live={watching.live} />
      )}

      {unpublishedEdit && !notice && (
        <Message level="info">
          You are looking at changes that are <strong>not on the live site yet</strong> — saved here,
          waiting to be published. The live page still shows the old version until somebody presses
          Publish.{" "}
          {previewBranch && <span className="hint mono">({previewBranch})</span>}
        </Message>
      )}

      <div className="card">
        <header>
          <h2>{entry.nameCard.title}</h2>
        </header>
        <div className="body">
          {entry.nameCard.showName && (
            <div className="field">
              <label htmlFor="f-name">{entry.nameLabel}</label>
              <input id="f-name" value={value("name") || ""} onChange={(e) => set("name", e.target.value)} />
              <FieldMessages findings={findings} field="name" />
            </div>
          )}
          {!entry.nameCard.showName && <FieldMessages findings={findings} field="name" />}
          <div className="field">
            <label htmlFor="f-slug">{entry.nameCard.slugLabel}</label>
            <input
              id="f-slug"
              className="mono"
              value={value("slug") || ""}
              disabled={everPublished || entry.nameCard.slugLocked}
              onChange={(e) => set("slug", e.target.value)}
            />
            <p className="help">
              {entry.nameCard.slugHelp
                ? entry.nameCard.slugHelp
                : everPublished
                ? "Locked. This page is in Google and in other people's links, so its address cannot change (ADR-0003). To retire it, unpublish — the old address keeps working as a redirect."
                : "Lower-case words joined by hyphens. Locked for good once this is published."}
            </p>
            <FieldMessages findings={findings} field="slug" />
          </div>
        </div>
      </div>

      {form.map((section) => (
        <div className="card" key={section.title}>
          <header>
            <h2>{section.title}</h2>
            {section.help && <p className="help">{section.help}</p>}
          </header>
          <div className="body">
            {section.intro && (
              <ul className="checklist">
                {section.intro.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {section.fields.map((field) => (
              <Field
                key={field.slug}
                field={field}
                schema={schema}
                references={references}
                value={value(field.slug)}
                onChange={(next) => set(field.slug, next)}
                findings={findings}
                allowJsonLd={session.can.includes("jsonld")}
              />
            ))}
            {(section.preview || section.title.startsWith("Search")) && url && (
              <SearchPreview url={url} title={titleValue} description={descriptionValue} />
            )}
          </div>
        </div>
      ))}

      <div className="sticky-actions spread">
        <div className="row">
          {/* On a published page, "Save" cannot mean "save a draft" — there is
              no draft of a live page. It saves to that page's own preview
              instead, and says so. Nothing but Publish touches the live site. */}
          <button className="primary" disabled={busy !== null} onClick={() => act("draft")}>
            {busy === "draft" ? "Saving…" : published ? "Save (not live yet)" : "Save draft"}
          </button>
          {may("preview") && (
            <button disabled={busy !== null} onClick={() => act("preview")}>
              {busy === "preview" ? "Building…" : "Preview"}
            </button>
          )}
          {may("publish") ? (
            <button className="primary" disabled={busy !== null} onClick={() => act("publish")}>
              {busy === "publish" ? "Publishing…" : published ? "Publish changes" : "Publish"}
            </button>
          ) : (
            <button disabled={busy !== null} onClick={askPublisher}>
              Ask a Publisher to publish
            </button>
          )}
        </div>
        <div className="row">
          <button className="quiet" onClick={showHistory}>
            History
          </button>
          {may("unpublish") && published && (
            <button className="danger" disabled={busy !== null} onClick={unpublish}>
              Unpublish
            </button>
          )}
          {dirty && <span className="hint">Unsaved changes</span>}
        </div>
      </div>

      {history && (
        <div className="card">
          <header>
            <h2>Changes to this {entry.one.toLowerCase()}</h2>
            <p className="help">From the repository's own history, so it is what actually happened. Vancouver time.</p>
          </header>
          <div className="body" style={{ padding: 0 }}>
            {history === "loading" ? (
              <div className="empty">Loading…</div>
            ) : !history.length ? (
              <div className="empty">No changes recorded for this {entry.one.toLowerCase()} yet.</div>
            ) : (
              <table>
                <tbody>
                  {history.map((c) => (
                    <tr key={c.sha}>
                      <td className="mono">{formatDateTime(c.date)}</td>
                      <td>{c.subject}</td>
                      <td>{c.by || c.author}</td>
                      <td>
                        <a href={c.url} target="_blank" rel="noopener">
                          view
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** One field, drawn according to its control. */
function Field({ field, schema, references, value, onChange, findings, allowJsonLd }) {
  const schemaField = (schema.fields || []).find((f) => f.slug === field.slug);
  const bad = (findings || []).some((f) => f.field === field.slug && f.level === "error");
  const id = `f-${field.slug}`;

  const options = useMemo(() => {
    // A select's choices: the form's own (a rating's 1 to 5), else the
    // schema's Option names, else the Live items the Reference can point at.
    if (field.options) return field.options;
    if (field.control === "select" && schemaField?.type === "Option") {
      return (schemaField.validations?.options || []).map((o) => ({ id: o.id, name: o.name }));
    }
    return references?.[field.slug] || [];
  }, [field, schemaField, references]);

  const wrap = (children) => (
    <div className={`field${bad ? " bad" : ""}`}>
      <div className="spread">
        <label htmlFor={id}>{field.label}</label>
        {field.softLimit && <Meter value={value} limit={field.softLimit} />}
      </div>
      {children}
      {field.help && <p className="help">{field.help}</p>}
      <FieldMessages findings={findings} field={field.slug} />
    </div>
  );

  switch (field.control) {
    case "rich":
      return (
        <div className={`field${bad ? " bad" : ""}`}>
          <label>{field.label}</label>
          <RichText value={value || ""} onChange={onChange} allowJsonLd={allowJsonLd} />
          <FieldMessages findings={findings} field={field.slug} />
        </div>
      );

    case "image":
      return (
        <>
          <ImageField label={field.label} help={field.help} value={value} onChange={onChange} decorative={field.decorative === true} />
          <FieldMessages findings={findings} field={field.slug} />
        </>
      );

    case "switch":
      return (
        <div className="field">
          <div className="switch">
            <input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
            <label htmlFor={id} style={{ marginBottom: 0 }}>
              {field.label}
            </label>
          </div>
          {field.help && <p className="help">{field.help}</p>}
        </div>
      );

    case "textarea":
      return wrap(
        <textarea id={id} rows={field.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      );

    case "number":
      return wrap(
        <input
          id={id}
          type="number"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        />
      );

    case "date":
      return wrap(
        <>
          <input
            id={id}
            type="datetime-local"
            value={toLocalInput(value)}
            onChange={(e) => onChange(e.target.value ? parseLocalInput(e.target.value) : null)}
          />
          <p className="help">Vancouver time, whatever this computer is set to.</p>
          {isFuture(value) && <Message level="info">{SCHEDULED_NOTICE}</Message>}
        </>
      );

    case "select": {
      // The form's own choices keep their type: a rating is stored as the
      // number 5, not the string "5", or the build's range check fails it.
      const pick = (raw) => {
        if (raw === "") return null;
        const chosen = field.options?.find((o) => String(o.id) === raw);
        return chosen ? chosen.id : raw;
      };
      return wrap(
        <select id={id} value={value ?? ""} onChange={(e) => onChange(pick(e.target.value))}>
          <option value="">— none —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      );
    }

    case "day":
      return wrap(
        <>
          <input id={id} type="date" value={toDayInput(value)} onChange={(e) => onChange(e.target.value ? parseDayInput(e.target.value) : null)} />
          <p className="help">A whole day, Vancouver time — through the end of it.</p>
        </>
      );

    case "multiselect": {
      const chosen = Array.isArray(value) ? value : [];
      return wrap(
        <select
          id={id}
          multiple
          size={Math.min(8, Math.max(3, options.length))}
          value={chosen}
          onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}
        >
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      );
    }

    case "url":
      return wrap(<input id={id} type="url" value={value || ""} onChange={(e) => onChange(e.target.value)} />);

    default:
      return wrap(<input id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} />);
  }
}
